import type { ItemFieldRow } from './item-field-reader';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import type { ComputeSourceCleanupDependencies } from './compute-source-cleanup';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath, removeIntegerCompilePath } from './integer-compute-slots';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger, phpAdd, phpSubtract } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { isPhpNumeric } from './php-decimal-values';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const arrayArg = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Computed persistence requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Computed path requires a scalar string'); return phpJsonString(v); };
function intArg(v: unknown): bigint {
    if (v == null || arrayLike(v) || !(typeof v === 'boolean' || typeof v === 'number' || isPhpNumeric(v))) throw new TypeError('Computed base requires an integer');
    const n = Number(v);
    if (!Number.isFinite(n) || n < -(2 ** 63) || n >= 2 ** 63) {
        if (typeof v !== 'string' || !/^[ \t\n\r\v\f]*[+-]?\d+[ \t\n\r\v\f]*$/.test(v) || BigInt(v.trim()) < -(2n ** 63n) || BigInt(v.trim()) >= 2n ** 63n) throw new TypeError('Computed base exceeds PHP integer range');
    }
    return phpJsonInteger(v);
}
const boolArg = (v: unknown): boolean => { if (v == null || arrayLike(v)) throw new TypeError('Computed flag requires a scalar'); return phpJsonTruthy(v); };
function totalArg(v: unknown): string | boolean | number {
    // Internal compilers retain native PHP integers as bigint until this store
    // point. JSON callers cannot supply bigint; project only after typed arithmetic.
    if (typeof v === 'bigint') return Number(v);
    if (v == null || arrayLike(v)) throw new TypeError('Computed total requires int|string|bool');
    if (typeof v === 'string' || typeof v === 'boolean') return v;
    if (typeof v === 'number' && Number.isFinite(v) && v >= -(2 ** 63) && v < 2 ** 63) return Number(phpJsonInteger(v));
    return phpJsonString(v);
}
/** Raw attribution cap: first highest equipped row wins. Unlike the equipped
 * fragment collector, brace metadata does not give a row precedence here.
 */
export function capInventorySourceRows(input: ItemFieldRow): [ItemFieldRow, number] {
    const rows = integerCompileWorkingRow(arrayArg(input)), keys = Object.keys(rows).filter(key => own(rows[key], 'kind') === 'inventory_equipped');
    if (keys.length <= 1) return [integerCompileResult(rows), 0];
    let sum: PhpArithmetic = 0n, winner: string | null = null, maximum: bigint | null = null;
    for (const key of keys) {
        const value = phpJsonInteger(own(rows[key], 'value') ?? 0); sum = phpAdd(sum, value);
        if (maximum === null || value > maximum) { maximum = value; winner = key; }
    }
    for (const key of keys) if (key !== winner) {
        const row = rows[key] as Row;
        rows[key] = { ...row, raw_value: Number(phpJsonInteger(own(row, 'value') ?? 0)), value: 0, suppressed: true };
    }
    return [integerCompileResult(integerCompileWorkingRow(Object.values(rows))), Number(phpSubtract(sum, maximum ?? 0n))];
}
export function computeSourceBreakdown(input: ItemFieldRow): ItemFieldRow {
    const values = new Map<string, PhpArithmetic>();
    for (const row of Object.values(arrayArg(input))) {
        const key = phpJsonString(own(row, 'category') ?? own(row, 'kind') ?? ''), value = phpJsonInteger(own(row, 'value') ?? 0);
        values.set(key, phpAdd(values.get(key) ?? 0n, value));
    }
    return integerCompileResult(Object.fromEntries([...values].map(([key, value]) => [key, Number(value)])));
}
export interface ComputedPersistenceOptions {
    base?: unknown; sources?: unknown; cap?: unknown; locked?: unknown; forceWrapper?: unknown; extra?: unknown;
}
/** Actual store point: fold unleaned source categories, then store the lean rows.
 * Returns a detached carrier; database persistence is outside this compiler.
 */
export function createComputedPersistence(deps: ComputeSourceCleanupDependencies, cleaner = createComputeSourceCleaner(deps)) {
    return function persist(input: ItemFieldRow, rawCluster: string, rawName: string, rawTotal: unknown, options: ComputedPersistenceOptions = {}): ItemFieldRow {
        const inputRow = arrayArg(input), cluster = stringArg(rawCluster), name = stringArg(rawName), total = totalArg(rawTotal);
        const base = intArg(options.base === undefined ? 0 : options.base), sources = arrayArg(options.sources === undefined ? [] : options.sources);
        const cap = options.cap === undefined || options.cap === null ? null : arrayArg(options.cap);
        const locked = boolArg(options.locked === undefined ? false : options.locked), force = boolArg(options.forceWrapper === undefined ? false : options.forceWrapper);
        const extra = arrayArg(options.extra === undefined ? [] : options.extra), out = integerCompileWorkingRow(inputRow), path = ['computed', 'creature', cluster, name];
        const empty = !Object.keys(sources).length && cap === null && !locked && base === 0n && !Object.keys(extra).length;
        if ((total === 0 || total === '' || total === false) && empty) { removeIntegerCompilePath(out, path); return integerCompileResult(out); }
        if (!force && empty) { putIntegerCompilePath(out, path, total); return integerCompileResult(out); }
        const payload: Row = {total, base: Number(base), locked}, lean = cleaner.clean(sources, out), breakdown = computeSourceBreakdown(sources);
        if (Object.keys(lean).length) payload.sources = lean;
        if (Object.keys(breakdown).length) payload.breakdown = breakdown;
        if (cap !== null) payload.cap = cap;
        for (const [key, value] of Object.entries(extra)) Object.defineProperty(payload, key, {value, enumerable: true, writable: true, configurable: true});
        putIntegerCompilePath(out, path, payload); return integerCompileResult(out);
    };
}
